import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/context/AuthContext';
import { useClientData } from './useClientData';
import { clientFormSchema, type ClientFormData } from '../forms/ClientFormValidation';
import { ClientType } from '@/types/entities/client';
import { useClientBirthdayTasks } from '@/services/tasks/clientBirthdayTaskService';
import { getValidRentalPointId } from '@/utils/userRentalPoint';

export interface UseAddClientDialogProps {
  onClientCreated?: (clientId: string) => void;
  onClose: () => void;
}

export const useAddClientDialogNew = ({ onClientCreated, onClose }: UseAddClientDialogProps) => {
  const [clientType, setClientType] = useState<'individual' | 'legal'>('individual');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<ClientFormData>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: {
      type: 'individual', // Добавляем обязательное поле type
      fullName: '',
      companyName: '',
      email: '',
      phone: '',
      address: '',
      comment: '',
      birthDate: '',
      remindBirthday: false,
      remindDaysBefore: 3,
      passportSeries: '',
      passportNumber: '',
      passportIssuedBy: '',
      passportIssueDate: '',
      bankName: '',
      bankAccount: '',
      bankBik: '',
      inn: '',
      kpp: '',
      legalEntityType: null,
      loyaltyStatus: 'white',
      status: 'active'
    }
  });

  const { createClient } = useClientData();
  const { user } = useAuth();
  const { createBirthdayReminderTask } = useClientBirthdayTasks();

  const onSubmit = async () => {
    console.log('🚀🚀🚀 [CLIENT CREATION] STEP 1: onSubmit called 🚀🚀🚀');
    setIsSubmitting(true);
    console.log('🚀 [CLIENT CREATION] STEP 2: setIsSubmitting(true) called');
    
    try {
      console.log('🚀 [CLIENT CREATION] STEP 3: Entering try block');
      
      // Get and validate form data
      console.log('🚀 [CLIENT CREATION] STEP 4: Getting form data...');
      const formData = form.getValues();
      console.log('📝 [CLIENT CREATION] STEP 5: Form data retrieved:', formData);
      
      console.log('🚀 [CLIENT CREATION] STEP 6: Validating form data...');
      const validatedData = clientFormSchema.parse(formData);
      console.log('✅ [CLIENT CREATION] STEP 7: Form validation passed:', validatedData);
      
      // Validate required fields
      if (!user?.id) {
        console.error('User ID is required to create a client');
        return;
      }
      
      // STEP 8: Ensure rental_point exists (client references rental_points table)
      console.log('🔧 [CLIENT CREATION] STEP 8: Ensuring rental_point exists (foreign key points to rental_points table)...');

      let rentalPointId: string | null = null;

      try {
        // Use centralized resolver with fallbacks and detailed logging
        rentalPointId = await getValidRentalPointId(user.id);
        console.log('🔧 [CLIENT CREATION] STEP 9: getValidRentalPointId result:', rentalPointId);

        if (!rentalPointId) {
          console.error('❌ [CLIENT CREATION] No valid rental_point could be resolved for user:', user.id);
          throw new Error('Не найден пункт проката для пользователя. Пожалуйста, создайте пункт проката в настройках.');
        }

        console.log('✅ [CLIENT CREATION] STEP 10: Using rental_point:', rentalPointId);
      } catch (error) {
        console.error('❌ [CLIENT CREATION] Rental point resolution error:', error);
        alert(`Ошибка при работе с пунктом проката: ${error instanceof Error ? error.message : String(error)}`);
        return;
      }
      
      // STEP 13: Prepare client data with proper types
      console.log('🔧 [CLIENT CREATION] STEP 13: Preparing client data...');
      const clientData = {
        ...validatedData,
        userId: user.id,
        rentalPointId, // Use valid rental point ID from database
        birthDate: validatedData.birthDate ? new Date(validatedData.birthDate) : null,
        passportIssueDate: validatedData.passportIssueDate ? new Date(validatedData.passportIssueDate) : null,
        registrationDate: new Date(),
        contacts: null,
      };

      console.log('🔧 [CLIENT CREATION] STEP 14: Client data prepared:', clientData);
      console.log('🔧 [CLIENT CREATION] STEP 15: Calling createClient...');
      
      const result = await createClient(clientData);
      
      console.log('🔧 [CLIENT CREATION] STEP 16: createClient completed, result:', result);
      
      if (result?.id) {
        // Автоматическое создание задачи-напоминания о дне рождения (если включено)
        try {
          if (validatedData.remindBirthday && validatedData.birthDate && validatedData.fullName) {
            console.log('🎂 [CLIENT CREATION] Creating birthday reminder task...');
            
            await createBirthdayReminderTask({
              clientId: result.id,
              clientName: validatedData.fullName,
              birthDate: new Date(validatedData.birthDate),
              remindDaysBefore: validatedData.remindDaysBefore || 3
            });
            
            console.log('✅ [CLIENT CREATION] Birthday reminder task created successfully');
          }
        } catch (error) {
          console.warn('⚠️ [CLIENT CREATION] Failed to create birthday reminder task:', error);
          // Не прерываем процесс создания клиента из-за ошибки создания задачи
        }
        
        onClientCreated?.(result.id);
        form.reset();
        onClose();
      } else {
        console.error('Client creation failed: no ID returned');
      }
    } catch (error) {
      console.error('Error creating client:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClientTypeChange = (type: string) => {
    const clientType = type as 'individual' | 'legal';
    setClientType(clientType);
    // Синхронизируем с формой
    form.setValue('type', clientType);
  };

  const loadDraft = () => {
    // Mock draft loading
    return false;
  };

  const clearDraft = () => {
    // Mock draft clearing
  };

  return {
    form,
    clientType,
    isSubmitting,
    onSubmit,
    handleClientTypeChange,
    loadDraft,
    clearDraft
  };
};
